import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Files,
  MessageSquare,
  Settings,
  Users,
  Building2,
  ChevronDown,
  LogOut,
  Workflow,
  Calendar,
  CalendarDays,
  ClipboardList,
  Disc3,
  AudioWaveform,
  FileSignature,
  Inbox,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useRole } from '@/hooks/portal/useRole';
import { usePortalAuth } from '@/hooks/portal/usePortalAuth';
import logoWhite from '@/assets/logo-white.png';

const mainNavItems = [
  { title: 'Dashboard', url: '/portal', icon: LayoutDashboard },
  { title: 'Releases', url: '/portal/releases', icon: Disc3 },
  { title: 'Mastering', url: '/portal/mastering', icon: AudioWaveform },
  { title: 'Files', url: '/portal/files', icon: Files },
  { title: 'Invoices', url: '/portal/invoices', icon: FileText },
  { title: 'Contracts', url: '/portal/contracts', icon: FileSignature },
  { title: 'Calendar', url: '/portal/calendar', icon: CalendarDays },
  { title: 'Support', url: '/portal/support', icon: MessageSquare },
];

const adminNavItems = [
  { title: 'Users', url: '/portal/admin/users', icon: Users },
  { title: 'Artists', url: '/portal/admin/organizations', icon: Building2 },
  { title: 'Applications', url: '/portal/admin/applications', icon: Inbox },
  { title: 'All Mastering', url: '/portal/admin/mastering', icon: AudioWaveform },
  { title: 'Modules', url: '/portal/admin/modules', icon: Workflow },
  { title: 'Bookings', url: '/portal/admin/bookings', icon: ClipboardList },
  { title: 'Booking Setup', url: '/portal/admin/booking-settings', icon: Calendar },
];

export function PortalSidebar() {
  const location = useLocation();
  const { isAdmin } = useRole();
  const { profile, signOut } = usePortalAuth();

  const getInitials = (name: string | null | undefined) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-sidebar-border p-2">
        <Link to="/portal" className="flex items-center justify-center">
          <img src={logoWhite} alt="Nail Point Records" className="h-7 group-data-[collapsible=icon]:h-6" />
        </Link>
      </SidebarHeader>

      <SidebarContent className="gap-0">
        <SidebarGroup className="py-1 px-1.5">
          <SidebarGroupLabel className="text-[10px] uppercase tracking-wider h-6">Main</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="gap-0.5">
              {mainNavItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    size="sm"
                    isActive={location.pathname === item.url}
                    tooltip={item.title}
                  >
                    <Link to={item.url}>
                      <item.icon className="h-4 w-4" />
                      <span className="text-xs">{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {isAdmin && (
          <SidebarGroup className="py-1 px-1.5">
            <SidebarGroupLabel className="text-[10px] uppercase tracking-wider h-6">Admin</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="gap-0.5">
                {adminNavItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      size="sm"
                      isActive={location.pathname === item.url}
                      tooltip={item.title}
                    >
                      <Link to={item.url}>
                        <item.icon className="h-4 w-4" />
                        <span className="text-xs">{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-1.5">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton size="sm" className="w-full">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={profile?.avatar_url ?? undefined} />
                    <AvatarFallback className="bg-sidebar-accent text-sidebar-accent-foreground text-[10px]">
                      {getInitials(profile?.full_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col items-start text-left min-w-0 flex-1">
                    <span className="text-xs font-medium truncate w-full">
                      {profile?.full_name ?? 'User'}
                    </span>
                  </div>
                  <ChevronDown className="h-3 w-3 shrink-0" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/portal/settings" className="flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => signOut()}
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
